import { useEffect, useRef, useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FloatingActionButton } from "@/components/common/floating-action-button/floating-action-button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyReportsState } from "@/components/reports/empty-reports-state/empty-reports-state";
import { ReportsOfflineState } from "@/components/reports/reports-offline-state/reports-offline-state";
import { CollectionRow } from "@/components/reports/collection-row/collection-row";
import {
  CollectionTableFrame,
  CollectionTableHeader,
} from "@/components/reports/collection-table-row/collection-table-row";
import { NewReportSheet } from "@/components/reports/new-report-sheet/new-report-sheet";
import {
  ReportKindFilter,
  type ReportKindSelection,
} from "@/components/reports/report-kind-filter/report-kind-filter";
import { DESKTOP_QUERY } from "@/hooks/use-is-desktop";
import { useMediaQuery } from "@/hooks/use-media-query";
import { usePermissions } from "@/hooks/use-permissions";
import { useOnlineStatus } from "@/offline/online-status";
import { useProjectCollections } from "./use-project-collections";
import type { CollectionKind, ProjectDetail } from "@/types/api";

interface ReportsViewProps {
  projectId: string;
  project: ProjectDetail;
  /** The kind the list is narrowed to; `null` for All. Kept in the URL by the
      route. See [[reports-view.spec]] § Filtering by kind. */
  kind?: ReportKindSelection;
  onKindChange?: (kind: ReportKindSelection) => void;
}

// The filter speaks the app's words; the API calls a photos report `records`.
const API_KIND: Record<"issues" | "photos", CollectionKind> = {
  issues: "issues",
  photos: "records",
};

/**
 * The Reports page.
 *
 * It lists **reports you can come back to**, not files. Each row is a
 * collection carrying the state of its most recent export; the files it has
 * produced live on the report's own page. Reports generated before collections
 * shipped are not carried across and are not listed here — see
 * [[report-collections.spec]] and the cutover notice below.
 */
export function ReportsView({
  projectId,
  project,
  kind = null,
  onKindChange,
}: ReportsViewProps) {
  const [newReportOpen, setNewReportOpen] = useState(false);
  const { can } = usePermissions({
    owner: project.owner.id,
    my_roles: project.my_roles,
  });
  const canCreate = can("report:Create");
  const isOnline = useOnlineStatus();
  // A table from the app's desktop line (where the side rail replaces the
  // bottom bar), cards below it. The view decides, not the row, because the
  // table needs a frame and a header around its rows. See
  // [[reports-view.spec]] § The list is a table on desktop.
  const asTable = useMediaQuery(DESKTOP_QUERY);

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useProjectCollections(projectId);

  const collections = data?.collections ?? [];

  // Filtered in the browser: the list endpoint takes no kind, and at 100 a
  // page almost every project arrives whole. Per-kind counts are only true
  // once every page is in, so until then they are left off rather than
  // counting the first 100 (FE-DEBT-041).
  const visible =
    kind === null
      ? collections
      : collections.filter((collection) => collection.kind === API_KIND[kind]);
  const allLoaded = !hasNextPage;
  const issuesCount = collections.filter((c) => c.kind === "issues").length;
  const counts = {
    all: data?.totalCount,
    issues: allLoaded ? issuesCount : undefined,
    photos: allLoaded ? collections.length - issuesCount : undefined,
  };

  // A report holds one kind for life, so New Report asks which before it opens
  // a picker. See [[new-report-sheet.spec]].
  const handleNewReport = () => setNewReportOpen(true);
  const newReportSheet = canCreate && (
    <NewReportSheet
      projectId={projectId}
      open={newReportOpen}
      onOpenChange={setNewReportOpen}
    />
  );

  // Reports are network-only — not persisted to the offline cache, and an
  // export's file + thumbnail are server-hosted. Show an honest offline state
  // ahead of every other branch; it falls through automatically on reconnect.
  if (!isOnline) {
    return (
      <ReportsLayout>
        <div className="min-h-0 flex-1">
          <ReportsOfflineState />
        </div>
      </ReportsLayout>
    );
  }

  if (isLoading) {
    return (
      <ReportsLayout>
        <div className="flex w-full flex-col gap-2 px-4 pt-2 pb-6">
          {asTable ? (
            <CollectionTableFrame>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-none" />
              ))}
            </CollectionTableFrame>
          ) : (
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))
          )}
        </div>
      </ReportsLayout>
    );
  }

  if (isError) {
    return (
      <ReportsLayout>
        <div className="flex w-full flex-col items-center justify-center gap-4 px-4 py-16 text-center">
          <h3 className="text-lg font-semibold">Something went wrong</h3>
          <p className="text-sm text-muted-foreground">
            An error occurred while loading reports. Please try again.
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </ReportsLayout>
    );
  }

  if (collections.length === 0) {
    return (
      <ReportsLayout>
        <div className="min-h-0 flex-1">
          <EmptyReportsState
            projectName={project.name}
            action={
              canCreate ? (
                <Button onClick={handleNewReport}>
                  <Plus className="mr-1 h-4 w-4" />
                  New Report
                </Button>
              ) : undefined
            }
          />
        </div>
        {newReportSheet}
      </ReportsLayout>
    );
  }

  return (
    <ReportsLayout>
      {/* Stays put under the title while the list scrolls, like the Issues
          page's status row. */}
      <div className="shrink-0">
        <div className="w-full px-4 pb-2">
          <ReportKindFilter
            value={kind}
            onChange={(next) => onKindChange?.(next)}
            counts={counts}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="flex w-full flex-col gap-2 px-4 pt-2 pb-2">
          {asTable ? (
            // Nothing to frame when the selected kind has no reports: a header
            // over no rows says nothing, and the "none yet" line is below.
            visible.length > 0 && (
              <CollectionTableFrame>
                <CollectionTableHeader />
                {visible.map((collection) => (
                  <CollectionRow
                    key={collection.id}
                    projectId={projectId}
                    collection={collection}
                    layout="table"
                  />
                ))}
                {isFetchingNextPage && (
                  <Skeleton className="h-16 w-full rounded-none" />
                )}
              </CollectionTableFrame>
            )
          ) : (
            visible.map((collection) => (
              <CollectionRow
                key={collection.id}
                projectId={projectId}
                collection={collection}
              />
            ))
          )}
          {hasNextPage && (
            <InfiniteScrollSentinel
              onEnter={fetchNextPage}
              disabled={isFetchingNextPage}
            />
          )}
          {isFetchingNextPage && !asTable && (
            <div className="flex justify-center py-4">
              <Skeleton className="h-24 w-full" />
            </div>
          )}
          {/* Only once every page is in: before that, the next page may hold
              one, and the sentinel above is already asking for it. */}
          {kind !== null && visible.length === 0 && allLoaded && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {kind === "issues" ? "No issue reports yet" : "No photo reports yet"}
            </p>
          )}
          <ArchivedReportsNotice />
        </div>
      </div>

      {canCreate && (
        <FloatingActionButton onClick={handleNewReport}>
          <Plus className="mr-1 h-5 w-5" />
          New Report
        </FloatingActionButton>
      )}
      {newReportSheet}
    </ReportsLayout>
  );
}

/**
 * The page frame every state renders in: the "Reports" title on top, the
 * state's own content below. Same title row as the Issues page, so the two
 * tabs read alike. See [[reports-view.spec]] § Layout.
 */
function ReportsLayout({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex h-full flex-1 flex-col">
      <div className="shrink-0">
        <div className="flex h-14 w-full items-center px-4">
          <h1 className="text-xl font-semibold tracking-[-0.02em]">Reports</h1>
        </div>
      </div>
      {children}
    </div>
  );
}

/**
 * The cutover notice.
 *
 * Reports made before collections shipped are not migrated — an old report
 * does not record which template produced it, so a backfill could only invent
 * one. Saying so costs a line and turns "where did my reports go" into a
 * shrug; an empty list would read as data loss.
 */
function ArchivedReportsNotice() {
  return (
    <p className="px-1 py-4 text-center text-xs text-muted-foreground">
      Reports generated before this update are archived. Contact support if you
      need one.
    </p>
  );
}

interface InfiniteScrollSentinelProps {
  onEnter: () => void;
  disabled: boolean;
}

function InfiniteScrollSentinel({
  onEnter,
  disabled,
}: InfiniteScrollSentinelProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || disabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onEnter();
      },
      { rootMargin: "800px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onEnter, disabled]);

  return <div ref={ref} aria-hidden className="h-1 w-full" />;
}
